import React from 'react';
import { Search, Filter, EyeOff, ChevronLeft, Lock, PanelRightClose, X, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';

export const FilterPanel = ({
  isOpen,
  setIsOpen,
  searchQuery,
  setSearchQuery,
  statusFilter,
  setStatusFilter,
  approvalFilter,
  setApprovalFilter,
  accountManagerFilter,
  setAccountManagerFilter,
  hideCompleted,
  setHideCompleted,
  hideApproved,
  setHideApproved,
  pipelineFilter,
  setPipelineFilter,
  stageFilter,
  setStageFilter,
  showNewDealSuspects,
  setShowNewDealSuspects,
  showNoLineCards,
  setShowNoLineCards,
  isAdmin,
  managerStats,
  clearFilters
}) => (
  <div className={`${isOpen ? 'w-64' : 'w-12'} flex-shrink-0 bg-gray-50 border-l border-gray-200 transition-all duration-300 flex flex-col`}>
    {isOpen ? (
      <>
        <div className="p-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </h2>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setIsOpen(false)}>
            <PanelRightClose className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-4">
          {/* Search */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Search</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 text-xs bg-white"
                data-testid="filter-search"
              />
            </div>
          </div>

          {/* Account Manager Filter - Admin Only */}
          {isAdmin && managerStats.length > 0 && (
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Account Manager</label>
              <Select value={accountManagerFilter} onValueChange={setAccountManagerFilter}>
                <SelectTrigger className="h-8 text-xs bg-white" data-testid="filter-manager">
                  <SelectValue placeholder="All Managers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Managers</SelectItem>
                  {managerStats.filter(m => m.emp_id && m.emp_id.trim()).map(m => (
                    <SelectItem key={m.emp_id} value={m.emp_id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Status Filter */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Status</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 text-xs bg-white" data-testid="filter-status">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Pipeline Filter */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Pipeline</label>
            <Select value={pipelineFilter} onValueChange={setPipelineFilter}>
              <SelectTrigger className="h-8 text-xs bg-white" data-testid="filter-pipeline">
                <SelectValue placeholder="All Pipelines" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Pipelines</SelectItem>
                <SelectItem value="NEW DEAL">New Deal</SelectItem>
                <SelectItem value="CROSS SELLING">Cross Selling</SelectItem>
                <SelectItem value="UP SELLING">Up Selling</SelectItem>
                <SelectItem value="WIN BACK">Win Back</SelectItem>
                <SelectItem value="INDENTING DEAL">Indenting Deal</SelectItem>
                <SelectItem value="REPEAT DEAL">Repeat Deal</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Stage Filter */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Stage</label>
            <Select value={stageFilter} onValueChange={setStageFilter}>
              <SelectTrigger className="h-8 text-xs bg-white" data-testid="filter-stage">
                <SelectValue placeholder="All Stages" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                <SelectItem value="SUSPECT">Suspect</SelectItem>
                <SelectItem value="SUSPECTS">Suspects</SelectItem>
                <SelectItem value="PROSPECT">Prospect</SelectItem>
                <SelectItem value="PROSPECTS">Prospects</SelectItem>
                <SelectItem value="NURTURING">Nurturing</SelectItem>
                <SelectItem value="EVALUATING">Evaluating</SelectItem>
                <SelectItem value="CLOSING">Closing</SelectItem>
                <SelectItem value="WON">Won</SelectItem>
                <SelectItem value="LOST">Lost</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Approval Filter */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Approval</label>
            <Select value={approvalFilter} onValueChange={setApprovalFilter}>
              <SelectTrigger className="h-8 text-xs bg-white" data-testid="filter-approval">
                <SelectValue placeholder="All Approval" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Approval</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="unapproved">Unapproved</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Toggles */}
          <div className="space-y-3 pt-2 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <label className="text-xs text-gray-600 flex items-center gap-1.5">
                <Eye className="h-3.5 w-3.5" />
                Show Suspects
              </label>
              <Switch checked={showNewDealSuspects} onCheckedChange={setShowNewDealSuspects} className="h-4 w-7" data-testid="toggle-show-suspects" />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-xs text-gray-600 flex items-center gap-1.5">
                <Eye className="h-3.5 w-3.5" />
                No Line Cards
              </label>
              <Switch checked={showNoLineCards} onCheckedChange={setShowNoLineCards} className="h-4 w-7" data-testid="toggle-no-line-cards" />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-xs text-gray-600 flex items-center gap-1.5">
                <EyeOff className="h-3.5 w-3.5" />
                Hide Completed
              </label>
              <Switch checked={hideCompleted} onCheckedChange={setHideCompleted} className="h-4 w-7" data-testid="toggle-hide-completed" />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-xs text-gray-600 flex items-center gap-1.5">
                <Lock className="h-3.5 w-3.5" />
                Hide Approved
              </label>
              <Switch checked={hideApproved} onCheckedChange={setHideApproved} className="h-4 w-7" data-testid="toggle-hide-approved" />
            </div>
          </div>

          {/* Clear Filters */}
          <Button variant="outline" size="sm" className="w-full h-8 text-xs" onClick={clearFilters} data-testid="clear-filters-btn">
            <X className="h-3 w-3 mr-1" />
            Clear All Filters
          </Button>
        </div>
      </>
    ) : (
      <div className="flex flex-col items-center py-3 gap-2">
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setIsOpen(true)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="writing-mode-vertical text-xs text-gray-500 font-medium tracking-wider" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
          FILTERS
        </div>
      </div>
    )}
  </div>
);
